import React, { useState } from "react";
import Select from "react-select";
import { getDisplayValue } from "../../../utils/displayValue";

const defaultProgrammeOptions = [
  { value: "Bachelors", label: "Bachelors" },
  { value: "Masters", label: "Masters" },
];

const PrimarySelection = ({
  countries = [],
  value,
  onChange,
  programmeOptions = defaultProgrammeOptions,
}) => {
  const [isFocused, setIsFocused] = useState({
    country: false,
    programme: false,
  });

  const [isTouched, setIsTouched] = useState({
    country: false,
    programme: false,
  });

  const fieldHasValue = (fieldValue) => Boolean(fieldValue);

  const handleFocus = (field) => {
    setIsFocused((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => {
    setIsFocused((prev) => ({ ...prev, [field]: false }));
    setIsTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getLabelStyles = (field) => {
    const hasValue = fieldHasValue(value?.[field]);

    if (isFocused[field]) {
      return {
        color: "#22c55e",
        top: "-10px",
        fontSize: "12px",
        zIndex: 20,
      };
    }

    if (isTouched[field] && !hasValue) {
      return {
        color: "#ef4444",
        top: "8px",
        fontSize: "14px",
        zIndex: 20,
      };
    }

    return {
      color: "#6b7280",
      top: hasValue ? "-10px" : "8px",
      fontSize: hasValue ? "12px" : "14px",
      zIndex: 20,
    };
  };

  const getBorderColor = (field) => {
    const hasValue = fieldHasValue(value?.[field]);

    if (isFocused[field]) {
      return "#22c55e";
    }

    if (isTouched[field] && !hasValue) {
      return "#ef4444";
    }

    return "#6b7280";
  };

  const getValidationMessage = (field) => {
    const hasValue = fieldHasValue(value?.[field]);
    const label = field.charAt(0).toUpperCase() + field.slice(1);

    return isTouched[field] && !hasValue
      ? `${label} is required`
      : `Please select ${label}`;
  };

  const commonSelectStyles = (field) => ({
    placeholder: "",
    styles: {
      control: (base) => ({
        ...base,
        minHeight: "42px",
        borderColor: getBorderColor(field),
        "&:hover": {
          borderColor: getBorderColor(field),
        },
        "&:focus-within": {
          borderColor: "#22c55e",
          boxShadow: "0 0 0 #22c55e",
        },
        zIndex: 10,
      }),
      menu: (base) => ({
        ...base,
        zIndex: 30,
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "white"
          : state.isFocused
          ? "#3265CB"
          : base.backgroundColor,
        color: state.isSelected
          ? "#374151"
          : state.isFocused
          ? "white"
          : base.color,
        "&:active": {
          backgroundColor: "#3265CB",
          color: "white",
        },
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
    },
  });

  return (
    <div className="mb-6">
      <h2 className="text-gray-800 text-lg font-semibold mb-4">
        Study Preferences *
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="relative">
          <span
            style={getLabelStyles("country")}
            className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
          >
            Country *
          </span>
          <Select
            id="primary-country"
            name="primary-country"
            options={countries.map((country) => ({
              value: getDisplayValue(country, ""),
              label: getDisplayValue(country, ""),
            }))}
            value={
              value?.country
                ? {
                    value: getDisplayValue(value.country, ""),
                    label: getDisplayValue(value.country, ""),
                  }
                : null
            }
            onChange={(selected) => {
              onChange("country", selected ? selected.value : "");
            }}
            onFocus={() => handleFocus("country")}
            onBlur={() => handleBlur("country")}
            {...commonSelectStyles("country")}
          />
          <span
            className="text-xs mt-1"
            style={{
              color:
                isTouched.country && !value?.country ? "#ef4444" : "#6b7280",
            }}
          >
            {getValidationMessage("country")}
          </span>
        </div>

        <div className="relative">
          <span
            style={getLabelStyles("programme")}
            className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
          >
            Programme *
          </span>
          <Select
            id="primary-programme"
            name="primary-programme"
            options={programmeOptions}
            value={
              value?.programme
                ? {
                    value: getDisplayValue(value.programme, ""),
                    label: getDisplayValue(value.programme, ""),
                  }
                : null
            }
            onChange={(selected) => {
              onChange("programme", selected ? selected.value : "");
            }}
            onFocus={() => handleFocus("programme")}
            onBlur={() => handleBlur("programme")}
            isDisabled={!value?.country}
            {...commonSelectStyles("programme")}
          />
          <span
            className="text-xs mt-1"
            style={{
              color:
                isTouched.programme && !value?.programme
                  ? "#ef4444"
                  : "#6b7280",
            }}
          >
            {getValidationMessage("programme")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default PrimarySelection;
